import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DocumentEditOperationDTO } from '@shared/schemas/document-model';
import { toast } from 'sonner';
import { ApiError } from '@/lib/api-error';
import { getChatErrorMessage } from '../chat-errors';
import {
  getDocumentGenerationModel,
  getDocumentGenerationPreviewHtml,
  submitDocumentEdits,
} from '../services/document-generation.api';

type SnapshotInput = {
  conversationId: string;
  enabled: boolean;
  generationId: string;
  /**
   * Clé de rechargement pendant la génération (`modelRevision` + étape) ; `undefined` pour une
   * version terminée, immuable.
   */
  revision: string | undefined;
};

/** Le backend répond 404 tant que le plan n'est pas persisté : « pas encore », pas une erreur. */
export function isNotReadyError(error: unknown) {
  return error instanceof ApiError && (error.status === 404 || error.status === 409);
}

const retryUnlessNotReady = (failureCount: number, error: unknown) => (
  !isNotReadyError(error) && failureCount < 1
);

/** Modèle structuré d'une version. Immuable pour (génération, révision) : jamais périmé. */
export function useGenerationModel({ conversationId, enabled, generationId, revision }: SnapshotInput) {
  return useQuery({
    enabled,
    placeholderData: keepPreviousData,
    queryFn: () => getDocumentGenerationModel(conversationId, generationId),
    queryKey: ['document-generation-model', conversationId, generationId, revision ?? 'final'],
    retry: retryUnlessNotReady,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/** HTML d'aperçu (srcdoc), rechargé quand la révision du modèle change. */
export function useGenerationPreview({ conversationId, enabled, generationId, revision }: SnapshotInput) {
  return useQuery({
    // Le HTML embarque ses polices : on ne le garde pas longtemps en mémoire une fois quitté.
    gcTime: 60_000,
    enabled,
    // L'ancien rendu reste affiché pendant le chargement du suivant : pas de flash blanc.
    placeholderData: keepPreviousData,
    queryFn: () => getDocumentGenerationPreviewHtml(conversationId, generationId),
    queryKey: ['document-generation-preview', conversationId, generationId, revision ?? 'final'],
    retry: retryUnlessNotReady,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

type AcceptedEdit = {
  generatedDocumentId?: string;
  generationId: string;
  revisionNumber?: number;
};

/** Envoie des opérations d'édition : chaque requête crée une nouvelle version `pending`. */
export function useCanvasEdits(input: {
  baseGenerationId: string;
  conversationId: string;
  documentId: string | undefined;
  onAccepted: (accepted: AcceptedEdit) => void;
}) {
  const queryClient = useQueryClient();
  const { baseGenerationId, conversationId, documentId, onAccepted } = input;
  return useMutation({
    mutationFn: (operations: DocumentEditOperationDTO[]) => {
      if (!documentId) {
        throw new Error('document_family_unknown');
      }
      return submitDocumentEdits(conversationId, documentId, { baseGenerationId, operations });
    },
    onError: (error) => {
      toast.error(getChatErrorMessage(error));
    },
    onSuccess: (accepted) => {
      void queryClient.invalidateQueries({
        queryKey: ['generated-document-versions', conversationId, documentId],
      });
      onAccepted(accepted);
    },
  });
}
